import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../utils/apiClient';
import APIConstant from '../utils/pathConstants';
import CommonConfig from '../utils/constant';

export const DEFAULT_THEME = {
  buttonBg: '#ffc013',
  buttonText: '#1c515e',
  buttonHover: '#f4a300',
  sidebarBg: '#ffffff',
  sidebarText: '#1c515e',
  headerBg: '#ffffff',
  headerText: '#1c515e',
};

const CAMEL_TO_KEBAB = {
  buttonBg: '--button-bg',
  buttonText: '--button-text',
  buttonHover: '--button-hover',
  sidebarBg: '--sidebar-bg',
  sidebarText: '--sidebar-text',
  headerBg: '--header-bg',
  headerText: '--header-text',
};

const LS_KEY = 'edutattva_theme';

const applyThemeToDOM = (theme) => {
  const root = document.documentElement;
  Object.keys(CAMEL_TO_KEBAB).forEach((key) => {
    const val = theme?.[key] || DEFAULT_THEME[key];
    root.style.setProperty(CAMEL_TO_KEBAB[key], val);
  });
};

const clearThemeFromDOM = () => {
  const root = document.documentElement;
  Object.keys(CAMEL_TO_KEBAB).forEach((key) => {
    root.style.removeProperty(CAMEL_TO_KEBAB[key]);
  });
};

const buildThemeFromColors = (c) => ({
  buttonBg: c.buttonBg || DEFAULT_THEME.buttonBg,
  buttonText: c.buttonText || DEFAULT_THEME.buttonText,
  buttonHover: c.buttonHover || DEFAULT_THEME.buttonHover,
  sidebarBg: c.sidebarBg || DEFAULT_THEME.sidebarBg,
  sidebarText: c.sidebarText || DEFAULT_THEME.sidebarText,
  headerBg: c.headerBg || DEFAULT_THEME.headerBg,
  headerText: c.headerText || DEFAULT_THEME.headerText,
});

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  activeThemeId: '',
  reloadTheme: async () => {},
  applyDefaultTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [activeThemeId, setActiveThemeId] = useState('');
  const lastUserIdRef = useRef('');
  const activeThemeIdRef = useRef('');

  useEffect(() => {
    activeThemeIdRef.current = activeThemeId;
  }, [activeThemeId]);

  const applyDefaultTheme = useCallback(() => {
    clearThemeFromDOM();
    setTheme(DEFAULT_THEME);
    setActiveThemeId('');
    document.body.classList.remove('theme-active');
  }, []);

  const fetchActiveTheme = useCallback(async (userId) => {
    if (!userId) {
      applyDefaultTheme();
      return false;
    }
    try {
      const res = await api.post(APIConstant.path.getActiveTheme, { UserID: userId });
      if (res?.success) {
        if (res?.data?.theme?.Colors) {
          const next = buildThemeFromColors(res.data.theme.Colors);
          applyThemeToDOM(next);
          setTheme(next);
          setActiveThemeId(res.data.theme._id || '');
          document.body.classList.add('theme-active');
          try {
            localStorage.setItem(LS_KEY, JSON.stringify({ theme: next, id: res.data.theme._id }));
          } catch (_) {}
          return true;
        }
        applyDefaultTheme();
        try {
          localStorage.removeItem(LS_KEY);
        } catch (_) {}
        return false;
      }
    } catch (_) {
      try {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.theme) {
            applyThemeToDOM(parsed.theme);
            setTheme(parsed.theme);
            setActiveThemeId(parsed.id || '');
            document.body.classList.add('theme-active');
            return true;
          }
        }
      } catch (_) {}
    }
    applyDefaultTheme();
    return false;
  }, [applyDefaultTheme]);

  const reloadTheme = useCallback(async () => {
    const loginData = CommonConfig.loginData();
    const userId = loginData?._id;
    await fetchActiveTheme(userId);
  }, [fetchActiveTheme]);

  const syncActiveTheme = useCallback(async () => {
    const loginData = CommonConfig.loginData();
    const userId = loginData?._id;
    if (!userId) return;
    let res;
    try {
      res = await api.post(APIConstant.path.getActiveTheme, { UserID: userId });
    } catch (_) {
      return;
    }
    if (!res?.success) return;
    const serverId = res?.data?.theme?._id ? String(res.data.theme._id) : '';
    if (serverId === (activeThemeIdRef.current || '')) return;
    if (serverId && res?.data?.theme?.Colors) {
      const next = buildThemeFromColors(res.data.theme.Colors);
      applyThemeToDOM(next);
      setTheme(next);
      setActiveThemeId(serverId);
      document.body.classList.add('theme-active');
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ theme: next, id: serverId }));
      } catch (_) {}
    } else {
      applyDefaultTheme();
      try {
        localStorage.removeItem(LS_KEY);
      } catch (_) {}
    }
  }, [applyDefaultTheme]);

  useEffect(() => {
    const tick = () => {
      const loginData = CommonConfig.loginData();
      const userId = loginData?._id || '';
      if (userId !== lastUserIdRef.current) {
        lastUserIdRef.current = userId;
        fetchActiveTheme(userId);
      }
    };
    tick();
    const onStorage = (e) => {
      if (e.key === 'ERPLoginData' || e.key === LS_KEY) tick();
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(tick, 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [fetchActiveTheme]);

  useEffect(() => {
    const onFocus = () => syncActiveTheme();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(syncActiveTheme, 12000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [syncActiveTheme]);

  return (
    <ThemeContext.Provider
      value={{ theme, activeThemeId, reloadTheme, applyDefaultTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
